import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../App';
import { Card, Button, Badge } from '../components/UI';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { ALL_ORGS, DeclaredDiff, Task, TaxObligation } from '../types';
import { TaskList } from '../components/directory/Staff';
import { api } from '../services/api';
import { SettleDialog } from '../components/SettleDialog';
import { OBLIGATION_TITLES, daysBetween, formatDay, formatMoney, orgDisplayName, todayISO } from '../utils/format';

// Разница расчёта с уведомлением до 1 ₽ — округление (1С округляет по сотрудникам и видам начислений)
const ROUNDING_TOLERANCE = 1.005;

// Налоги к уплате по срокам: зарплатные, УСН, НДС, взносы ИП. Создаются и пересчитываются автоматически
export const TaxCalendarPage: React.FC = () => {
  const { state, revision, refreshData, showNotification } = useData();
  const [year, setYear] = useState(new Date().getFullYear());
  const [obligations, setObligations] = useState<TaxObligation[]>([]);
  const [ensWallet, setEnsWallet] = useState(0);
  const [paying, setPaying] = useState<TaxObligation | null>(null);
  // Расшифровка «расчёт − по уведомлению»: организация|налог|начало периода → операции
  const [diffs, setDiffs] = useState<Record<string, DeclaredDiff['items'][number]>>({});
  // Кадровые напоминания: ЕФС-1 после приёма/увольнения, выплата отпускных за 3 дня до отпуска
  const [tasks, setTasks] = useState<Task[]>([]);
  const loadTasks = useCallback(() => { api.getTasks().then(setTasks).catch(() => setTasks([])); }, []);
  useEffect(loadTasks, [loadTasks, revision]);
  const isAll = state.currentOrganizationId === ALL_ORGS;
  const orgOf = (id: string) => state.organizations.find(o => o.id === id);
  const today = todayISO();

  // Ответы для ранее выбранной организации, пришедшие после переключения, отбрасываются
  const loadSeq = useRef(0);
  const load = useCallback(() => {
      const orgId = state.currentOrganizationId;
      if (!orgId) return;
      const seq = ++loadSeq.current;
      const fresh = () => seq === loadSeq.current;
      api.getObligations(orgId).then(o => { if (fresh()) setObligations(o); }).catch(() => { if (fresh()) setObligations([]); });
      const ids = orgId === ALL_ORGS ? state.organizations.map(o => o.id) : [orgId];
      Promise.all(ids.map(id => api.declaredDiff(id).then(d => d.items.map(it => [`${id}|${it.type}|${it.periodStart}`, it] as const)).catch(() => [])))
          .then(parts => { if (fresh()) setDiffs(Object.fromEntries(parts.flat())); });
      if (orgId !== ALL_ORGS) {
          api.getReport(orgId, new Date().getFullYear())
              .then(r => { if (fresh()) setEnsWallet(r.obligations.ens_wallet); }).catch(() => { if (fresh()) setEnsWallet(0); });
      }
  }, [state.currentOrganizationId, state.organizations]);
  useEffect(load, [load, revision]);

  const afterChange = () => { load(); refreshData(); };

  // Подсказка: какие операции дают разницу расчёта приложения и поданного (по кварталу налога)
  const diffHint = (o: TaxObligation) => {
      const it = diffs[`${o.organizationId}|${o.type}|${o.periodStart}`];
      if (!it) return 'Расчёт приложения отличается от поданного';
      const lines = it.operations.map(op => `${formatDay(op.date)} ${op.description}: ${formatMoney(op.amount)} → ${formatMoney(op.effect)}`);
      if (Math.abs(it.other) >= 1) lines.push(`прочее (методика: уменьшение на взносы, отгрузки, округления): ${formatMoney(it.other)}`);
      return `За квартал: расчёт ${formatMoney(it.calculated)}, подано ${formatMoney(it.declared)}, разница ${formatMoney(it.diff)}\n` +
          (lines.length ? lines.join('\n') : 'Операций, не видных бухгалтеру, нет — разница в методике расчёта');
  };

  // Начислено по уведомлению: пусто — снова по расчёту приложения
  const saveDeclared = async (o: TaxObligation, raw: string) => {
      const v = raw.replace(/\s/g, '').replace(',', '.');
      const amount = v === '' ? null : Number(v);
      if (amount !== null && (Number.isNaN(amount) || amount < 0)) return;
      if (amount === (o.declaredAmount ?? null)) return;
      try { await api.setDeclared(o.id, amount); afterChange(); }
      catch (e: any) { showNotification(e.message || 'Не удалось сохранить', 'error'); }
  };

  const unsettle = async (o: TaxObligation) => {
      try { await api.unsettleObligation(o.id); afterChange(); }
      catch (e: any) { showNotification(e.message || 'Не удалось отменить', 'error'); }
  };

  const shown = useMemo(() => obligations.filter(o => o.dueDate.startsWith(String(year))), [obligations, year]);
  // Сверху — неоплаченные (просроченные и ближайшие по сроку), ниже — уплаченные, от свежих к старым
  const ordered = useMemo(() => {
      const pending = shown.filter(o => o.status !== 'SETTLED').sort((a, b) => a.dueDate.localeCompare(b.dueDate));
      const paid = shown.filter(o => o.status === 'SETTLED').sort((a, b) => b.dueDate.localeCompare(a.dueDate) || b.amount - a.amount);
      return [...pending, ...paid];
  }, [shown]);
  const pendingTotal = shown.filter(o => o.status === 'PENDING').reduce((s, o) => s + o.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar /> Налоговый календарь
            <span className="flex items-center bg-white rounded-md border border-slate-300 px-2 py-1 text-base">
                <button onClick={() => setYear(y => y - 1)} className="text-slate-400 hover:text-blue-600"><ChevronLeft size={16}/></button>
                <span className="mx-2">{year}</span>
                <button onClick={() => setYear(y => y + 1)} className="text-slate-400 hover:text-blue-600"><ChevronRight size={16}/></button>
            </span>
        </h1>
        <div className="text-sm text-slate-600 text-right">
            <div>К уплате: <strong className="text-red-600">{formatMoney(pendingTotal)}</strong></div>
            {!isAll && <div className="text-xs">Сальдо ЕНС: {formatMoney(ensWallet)}</div>}
        </div>
      </div>
      <p className="text-xs text-slate-500">
          Суммы пересчитываются автоматически по операциям; если введено «по уведомлению» — к уплате и зачёт из ЕНС по нему. «Оплатить» — со счёта (перевод на ЕНС или в СФР) или зачётом из сальдо ЕНС.
          Сроки перенесены с выходных, праздники не учитываются.
      </p>

      {tasks.length > 0 && (
          <div className="space-y-2">
              <h2 className="text-sm font-bold text-slate-500 uppercase">Кадровые задачи</h2>
              <TaskList tasks={tasks} onToggle={t => api.setTaskDone(t.id, !t.done).then(loadTasks).catch(e => showNotification(e?.message || 'Ошибка', 'error'))}
                  showWho={t => orgDisplayName(orgOf(t.organizationId || ''))} />
          </div>
      )}

      {shown.length === 0 && <Card className="p-6 text-center text-slate-400 text-sm">Налогов со сроком в {year} году нет</Card>}

      {ordered.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                  <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
                      <th className="text-left px-3 py-2 font-medium">Срок</th>
                      {isAll && <th className="text-left px-3 py-2 font-medium">ИП</th>}
                      <th className="text-left px-3 py-2 font-medium">Налог</th>
                      <th className="text-right px-3 py-2 font-medium" title="Расчёт налогового ядра по операциям">Расчёт</th>
                      <th className="text-right px-3 py-2 font-medium" title="Начислено по уведомлению / декларации. Если введено — к уплате и зачёт из ЕНС по этой сумме">По уведомлению</th>
                      <th className="text-right px-3 py-2 font-medium">{'К уплате / уплачено'}</th>
                      <th className="text-left px-3 py-2 font-medium">Статус</th>
                      <th className="px-3 py-2"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                      {ordered.map((o, i) => {
                          const paid = o.status === 'SETTLED';
                          const overdue = !paid && o.dueDate < today;
                          const days = daysBetween(today, o.dueDate);
                          const late = paid && o.settledOn ? daysBetween(o.dueDate, o.settledOn) : 0;
                          const firstPaid = paid && (i === 0 || ordered[i - 1].status !== 'SETTLED');
                          return (
                              <React.Fragment key={o.id}>
                                  {firstPaid && <tr><td colSpan={isAll ? 8 : 7} className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold uppercase text-slate-400">Уплачено</td></tr>}
                                  <tr className={paid ? 'text-slate-500' : overdue ? 'bg-red-50' : days <= 7 ? 'bg-amber-50' : ''}>
                                      <td className="px-3 py-2 whitespace-nowrap">{formatDay(o.dueDate)}
                                          {!paid && <div className="text-[11px] text-slate-500">{days >= 0 ? `через ${days} дн.` : `${-days} дн. назад`}</div>}</td>
                                      {isAll && <td className="px-3 py-2 whitespace-nowrap text-xs">{orgDisplayName(orgOf(o.organizationId))}</td>}
                                      <td className="px-3 py-2"><div className={paid ? '' : 'font-semibold text-slate-800'}>{OBLIGATION_TITLES[o.type]}</div>
                                          <div className="text-[11px] text-slate-500 break-words">{o.description}</div></td>
                                      <td className="px-3 py-2 text-right whitespace-nowrap text-xs text-slate-500">{o.calculatedAmount != null ? formatMoney(o.calculatedAmount) : '—'}</td>
                                      <td className="px-3 py-2 text-right whitespace-nowrap text-xs">
                                          <input key={`${o.id}-${o.declaredAmount}`} defaultValue={o.declaredAmount ?? ''} placeholder="ввести" inputMode="decimal"
                                              title={o.declaredSource ? `Источник: ${o.declaredSource}` : 'Сумма из уведомления / декларации'}
                                              className={`w-24 text-right rounded px-1 border ${o.declaredSource?.includes('проверьте') ? 'border-amber-300 bg-amber-50' : 'border-transparent hover:border-slate-200'} focus:border-blue-400`}
                                              onBlur={e => saveDeclared(o, e.target.value)} />
                                          {o.declaredSource && <div className="text-[10px] text-slate-400">{o.declaredSource}</div>}
                                          {o.declaredAmount != null && o.calculatedAmount != null && Math.abs(o.calculatedAmount - o.declaredAmount) >= 0.005 && (
                                              Math.abs(o.calculatedAmount - o.declaredAmount) <= ROUNDING_TOLERANCE
                                                  ? <div className="text-[10px] text-emerald-700" title={`Разница ${formatMoney(o.calculatedAmount - o.declaredAmount)} — округление (как в 1С)`}>
                                                      совпадает (округление)</div>
                                                  : <div className="text-[10px] text-amber-700 cursor-help" title={diffHint(o)}>
                                                      Разница с расчётом: {formatMoney(o.calculatedAmount - o.declaredAmount)}</div>
                                          )}
                                      </td>
                                      <td className={`px-3 py-2 text-right whitespace-nowrap ${paid ? '' : 'font-bold text-slate-900'}`}>{formatMoney(o.amount)}</td>
                                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                                          {paid ? <><Badge variant={late > 0 ? 'warning' : 'success'}>{late > 0 ? `с опозданием ${late} дн.` : 'Уплачен'}</Badge>
                                                    <div className="text-[11px] mt-0.5">{o.settleMethod === 'ENS' ? 'зачтён из ЕНС' : 'оплачен'} {formatDay(o.settledOn)}</div></>
                                                : overdue ? <Badge variant="danger">Просрочен</Badge> : <span className="text-slate-500">к уплате</span>}
                                      </td>
                                      <td className="px-3 py-2 text-right whitespace-nowrap">
                                          {paid ? <button onClick={() => unsettle(o)} className="text-xs text-slate-400 hover:text-red-600">Отменить</button>
                                                : <Button size="sm" onClick={() => setPaying(o)} className="h-6 text-[10px] px-2">Оплатить</Button>}
                                      </td>
                                  </tr>
                              </React.Fragment>
                          );
                      })}
                  </tbody>
              </table>
          </div>
      )}
      <SettleDialog obligation={paying} org={paying ? orgOf(paying.organizationId) : undefined} ensWallet={isAll ? undefined : ensWallet}
                    onClose={() => setPaying(null)} onDone={afterChange} />
    </div>
  );
};
